import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { progressService } from "../../../../../../services/progressService";
import { UserContext } from "../../../../../../UserContext";
import StakeholderProfileForm from "./StakeholderProfileForm";
import CompletionScreen from "./CompletionScreen";

const ProfileStep1Adapter = ({
  userId,
  subsectionId,
  onNextStep,
  onPreviousStep,
  onPhaseChange,
  initialSelectedStakeholders,
}) => {
  const [phase, setPhase] = useState("form");
  const [allStakeholdersData, setAllStakeholdersData] = useState({});
  const [selectedStakeholders, setSelectedStakeholders] = useState(
    initialSelectedStakeholders || [null, null]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useContext(UserContext);
  const userIdContext = user?.user_id;
  const effectiveUserId = userId || userIdContext;

  const debounceTimer = useRef(null);
  const PREVIEW_DURATION = 5000;

  console.log("🎯 ProfileStep1Adapter Props:");
  console.log("  ├─ initialSelectedStakeholders:", initialSelectedStakeholders);
  console.log("  └─ Stakeholder 1:", initialSelectedStakeholders?.[0]);

  useEffect(() => {
    if (onPhaseChange) onPhaseChange("immersive");
  }, [onPhaseChange]);

  // ✅ Update selectedStakeholders when initialSelectedStakeholders changes
  useEffect(() => {
    if (initialSelectedStakeholders) {
      console.log("✅ Using stakeholders from selection screen:", initialSelectedStakeholders);
      setSelectedStakeholders(initialSelectedStakeholders);
    }
  }, [initialSelectedStakeholders]);

  // ✅ Load saved data - ONLY allStakeholdersData (by ID)
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (!effectiveUserId || !subsectionId) {
          setAllStakeholdersData({});
          setIsLoading(false);
          return;
        }

        let res = null;
        try {
          res = await progressService.getUserProgress(
            effectiveUserId,
            subsectionId
          );
        } catch (err) {
          console.error(
            "[ProfileStep1Adapter] getUserProgress error (may be 404):",
            err
          );
        }

        if (!res) {
          console.log("[ProfileStep1Adapter] No existing progress, starting fresh");
          setAllStakeholdersData({});
          setIsLoading(false);
          return;
        }

        const payload = res?.data?.payload || res?.payload || null;
        console.log("[ProfileStep1Adapter] 📦 Loaded payload:", payload);

        if (payload?.allStakeholdersData) {
          console.log("✅ Loading allStakeholdersData:", payload.allStakeholdersData);
          setAllStakeholdersData(payload.allStakeholdersData);
        } else {
          setAllStakeholdersData({});
        }
      } catch (e) {
        console.error("[ProfileStep1Adapter] Failed to load data:", e);
        setError("Failed to load data");
        setAllStakeholdersData({});
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [effectiveUserId, subsectionId]);

  // ✅ FIX: Save in BOTH formats (by ID + by position)
  const saveProgress = useCallback(
    async (data, status = "in_progress") => {
      if (!effectiveUserId || !subsectionId) return;

      setIsSaving(true);
      try {
        const stakeholder1 = selectedStakeholders[0];
        const stakeholder2 = selectedStakeholders[1];

        // Build stakeholderData in position format
        const stakeholderData = {};
        
        if (stakeholder1?.id) {
          const s1Data = data.allStakeholdersData?.[stakeholder1.id] || allStakeholdersData[stakeholder1.id] || {};
          stakeholderData.stakeholder1 = {
            name: stakeholder1.name,
            role: stakeholder1.role,
            ...s1Data,
          };
        }

        if (stakeholder2?.id) {
          const s2Data = data.allStakeholdersData?.[stakeholder2.id] || allStakeholdersData[stakeholder2.id] || {};
          stakeholderData.stakeholder2 = {
            name: stakeholder2.name,
            role: stakeholder2.role,
            ...s2Data,
          };
        }

        const payloadToSave = {
          type: "widget",
          payload: {
            selectedStakeholders: selectedStakeholders,
            allStakeholdersData: data.allStakeholdersData ?? allStakeholdersData,
            stakeholderData: stakeholderData, // ✅ Added position-based format
          },
        };

        console.log(
          "[ProfileStep1Adapter] 💾 Saving progress with dual format:",
          payloadToSave
        );

        await progressService.saveProgress(
          effectiveUserId,
          subsectionId,
          payloadToSave,
          status
        );
      } catch (err) {
        console.error("[ProfileStep1Adapter] Save failed:", err);
        setError("Failed to save");
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [
      effectiveUserId,
      subsectionId,
      selectedStakeholders,
      allStakeholdersData,
    ]
  );

  const handleDataChange = useCallback(
    (data) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(() => {
        const stakeholder = selectedStakeholders[0];
        if (!stakeholder?.id) return;

        console.log("[ProfileStep1Adapter] 📝 Data changed for ID:", stakeholder.id, data);

        // ✅ Save by stakeholder ID, not position
        setAllStakeholdersData((prev) => ({
          ...prev,
          [stakeholder.id]: {
            name: stakeholder.name,
            role: stakeholder.role,
            ...(prev[stakeholder.id] || {}),
            ...data,
          },
        }));

        console.log("[ProfileStep1Adapter] 🔄 Data updated for:", stakeholder.name);
      }, 1000);
    },
    [selectedStakeholders]
  );

  // ✅ Auto-save effect
  useEffect(() => {
    if (Object.keys(allStakeholdersData).length === 0) return;
    
    const saveTimer = setTimeout(() => {
      if (selectedStakeholders[0]?.id || selectedStakeholders[1]?.id) {
        console.log("[ProfileStep1Adapter] 💾 Auto-saving progress");
        saveProgress({
          allStakeholdersData,
        });
      }
    }, 1000);

    return () => clearTimeout(saveTimer);
  }, [allStakeholdersData, saveProgress, selectedStakeholders]);

  const handleFormComplete = async (formData) => {
    const cleanedFormData = {
      values: (formData.values || []).filter((v) => v.trim() !== ""),
      kpis: (formData.kpis || []).filter((k) => k.trim() !== ""),
      interests: (formData.interests || []).filter((i) => i.trim() !== ""),
    };

    if (
      !cleanedFormData.values.length &&
      !cleanedFormData.kpis.length &&
      !cleanedFormData.interests.length
    ) {
      alert(
        "Please enter at least one value, KPI, or interest before saving."
      );
      return;
    }

    const stakeholder = selectedStakeholders[0];
    if (!stakeholder?.id) {
      alert("No stakeholder selected");
      return;
    }

    // ✅ Save by stakeholder ID
    const updatedAllStakeholdersData = {
      ...allStakeholdersData,
      [stakeholder.id]: {
        name: stakeholder.name,
        role: stakeholder.role,
        ...cleanedFormData,
      },
    };

    setAllStakeholdersData(updatedAllStakeholdersData);

    try {
      console.log("[ProfileStep1Adapter] ✅ Form completed, saving:", {
        stakeholderId: stakeholder.id,
        stakeholderName: stakeholder.name,
        data: updatedAllStakeholdersData[stakeholder.id],
      });

      await saveProgress(
        {
          allStakeholdersData: updatedAllStakeholdersData,
        },
        "in_progress"
      );
    } catch {
      alert("Error saving data");
      return;
    }

    setPhase("preview");
    setTimeout(() => setPhase("completion"), PREVIEW_DURATION);
  };

  const handleContinue = () => {
    if (typeof onNextStep === "function") onNextStep();
  };

  const handleEdit = () => {
    setPhase("form");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-red-50 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  const stakeholder = selectedStakeholders[0];
  if (!stakeholder) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <p className="text-red-500 mb-4">No stakeholder selected</p>
          <button className="btn" onClick={onPreviousStep}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ✅ CRITICAL: Load data based on stakeholder ID
  const initialData = allStakeholdersData[stakeholder.id] || {
    values: ["", "", ""],
    kpis: ["", "", ""],
    interests: ["", "", ""],
  };

  console.log("[ProfileStep1Adapter] 👤 Current stakeholder:", stakeholder);
  console.log("[ProfileStep1Adapter] 🔑 Stakeholder ID:", stakeholder.id);
  console.log(
    "[ProfileStep1Adapter] 📦 Data for this ID:",
    allStakeholdersData[stakeholder.id]
  );
  console.log(
    "[ProfileStep1Adapter] 📤 initialData passed to form:",
    initialData
  );

  return (
    <>
      {phase === "form" && (
        <StakeholderProfileForm
          key={`form-${stakeholder.id}`}
          stakeholder={stakeholder}
          stakeholderNumber={1}
          initialData={initialData}
          onComplete={handleFormComplete}
          onDataChange={handleDataChange}
          isSaving={isSaving}
          loading={false}
          onPreviousStep={onPreviousStep}
        />
      )}
      {phase === "preview" && (
        <CompletionScreen
          stakeholderData={initialData}
          stakeholder={stakeholder}
          stakeholderNumber={1}
          showEditProfile={false}
          showContinue={false}
          userName={user?.firstName || ""}
        />
      )}
      {phase === "completion" && (
        <CompletionScreen
          stakeholderData={initialData}
          stakeholder={stakeholder}
          stakeholderNumber={1}
          showEditProfile={true}
          showContinue={true}
          onNext={handleContinue}
          onEditProfile={handleEdit}
          userName={user?.firstName || ""}
          continueButtonText="Continue"
        />
      )}
    </>
  );
};

export default ProfileStep1Adapter;